/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState } from 'react'
import ProfilePicture from 'components/elements/ProfilePicture'
import { KnowStatus, KnowTranscript } from 'interfaces/Know'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { trackEvent } from 'lib/tracking/analytics-service'
import { knowEditRoute } from 'lib/constants/routes'
import { Body2, ButtonText } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import Stack from 'components/elements/Stack'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import _useTranslation from 'lib/hooks/_useTranslation'
import { AnalyticsEventKnowContentView, AnalyticsEventKnowConversionMobileContent } from 'lib/constants/AnalyticsEventType'
import NewButton from 'components/elements/NewButton'
import { Flashcard } from 'interfaces/Flashcard'
import KnowFlashcardPreview from 'components/modules/Knows/KnowFlashcardPreview'
import { returnKnowerFollowerLabel } from 'lib/formatting/knows'
import { SearchOptions } from 'interfaces/TrackingSnippets'
import { getCountryLanguageConfig } from 'lib/constants/countryLanguageConfig'
import dynamic from 'next/dynamic'
import KnowActions from '../SEO/KnowActions'
import BlurredMobileImage from '../SEO/BlurredMobileImage'
import { KnowerLink } from 'lib/features/Know/knower'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'

const KnowPreviewSection = dynamic(() => import('components/feature/Know/view/content/KnowPreviewSection'))

const HeaderSection = styled.div`
    width: fit-content;
    display: flex;
    justify-content: space-between;
    @media (max-width: 1050px) {
        padding: 5px 15px;
        flex-direction: column;
    }
    @media (max-width: 1050px) {
        padding: 10px 15px;
    }
`

const KnowerSectionWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    grid-gap: 10px;
`

const KnowerCredentials = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 10px;
`

const KnowArea = styled.div`
    height: auto;
    background-color: ${theme.colors.violetBackground};
    display: flex;
    justify-content: center;
    align-items: center;
    overflow-y: hidden;
    min-width: 400px;
    position: relative;
    padding: 10px;
    @media (max-width: 1050px) {
        min-width: 200px;
    }
`

const KnowDescription = styled(Body2)`
    padding: 5px 15px;
    overflow-wrap: anywhere;
`

interface Props {
    knowSeoInfo: KnowSeoInfo
    isOwner?: boolean
    onContentConversionBannerButtonClick(platform: 'android' | 'ios'): void
    referrerScreen?: string
    isMobile?: boolean
    downloadDeepLinkPath: string
    showPreview?: boolean
    transcript?: KnowTranscript | null
    flashcards?: Flashcard[] | null
    search?: SearchOptions
}

const KnowContent = ({
    knowSeoInfo,
    isOwner,
    onContentConversionBannerButtonClick,
    referrerScreen,
    isMobile,
    downloadDeepLinkPath,
    showPreview,
    transcript,
    flashcards,
    search,
}: Props) => {
    const [showContent, setShowContent] = useState(false)
    const router = useRouter()
    const { locale } = router
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const countryLanguageConfig = getCountryLanguageConfig(locale)
    const isLaunched = !!countryLanguageConfig?.isLaunched
    const { t } = _useTranslation('knows')
    const { know } = knowSeoInfo

    const showEditButton =
        know.status !== KnowStatus.PermanentlyRejected && know.status !== KnowStatus.Public && !flashcards && isOwner

    // This useEffect makes sure that the PDF preview is not loaded for the initial
    // SEO Know Page view. so that additional LCS and loading time is not required.
    // Wrapping in setTimeout defers the function to the end of the execution queue.
    useEffect(() => {
        if (authenticatedUser || !!showPreview || !isLaunched) setTimeout(() => setShowContent(true), 0)
    }, [authenticatedUser, isLaunched, isMobile, showPreview])

    const handleEditClick = () => router.push(knowEditRoute(know.uuid))

    const trackContentOpenEvent = () =>
        trackEvent(isMobile ? AnalyticsEventKnowConversionMobileContent : AnalyticsEventKnowContentView, {
            know,
            search,
        })

    const KnowPreview = () => (
        <KnowPreviewSection
            knowSeoInfo={knowSeoInfo}
            trackContentOpenEvent={trackContentOpenEvent}
            onContentConversionBannerButtonClick={onContentConversionBannerButtonClick}
            showFixedBanner={isLaunched}
            disableExperiments={showPreview}
            transcript={transcript}
            similarKnows={null}
        />
    )

    const KnowerSection = () => (
        <KnowerSectionWrapper>
            <KnowerLink knower={know.knower}>
                <KnowerCredentials>
                    <ProfilePicture
                        user={know.knower.user}
                        size={isMobile ? 35 : 70}
                        borderColor={theme.colors.knowunityBlue}
                    />
                    <Stack gutter={0}>
                        <Body2 color={theme.colors.headlineDark} fontWeight={600} fontFamily="var(--font-poppins)">
                            {know.knower.user.name ?? know.knower.user.username}
                        </Body2>
                        <Body2 color={theme.colors.body2Black} fontFamily="var(--font-inter)" fontWeight={400}>
                            {returnKnowerFollowerLabel(know, t, locale)}
                        </Body2>
                    </Stack>
                </KnowerCredentials>
            </KnowerLink>
            {showEditButton ? (
                <NewButton color={theme.colors.white} type="secondary" height={40} borderRadius={theme.borderRadius.normal}>
                    <ButtonText
                        fontFamily="var(--font-inter)"
                        onClick={handleEditClick}
                        color={theme.colors.deepSeaBlue}
                        fontWeight={700}
                    >
                        {t('knows/knowDetailsEditButton')}
                    </ButtonText>
                </NewButton>
            ) : null}
        </KnowerSectionWrapper>
    )

    return (
        <div>
            <HeaderSection data-nosnippet="true">
                <KnowerSection />
            </HeaderSection>

            {showContent ? (
                <KnowArea>
                    {flashcards ? <KnowFlashcardPreview know={know} flashcards={flashcards} /> : <KnowPreview />}
                </KnowArea>
            ) : (
                <>
                    {isMobile ? (
                        <BlurredMobileImage
                            know={know}
                            referrerScreen={referrerScreen}
                            downloadDeepLinkPath={downloadDeepLinkPath}
                            transcript={transcript}
                        />
                    ) : (
                        <KnowArea>
                            {flashcards ? <KnowFlashcardPreview know={know} flashcards={flashcards} /> : <KnowPreview />}
                        </KnowArea>
                    )}
                </>
            )}

            {isLaunched ? <KnowActions isMobile={!!isMobile} knowSeoInfo={knowSeoInfo} showPreview={!!showPreview} /> : null}

            <KnowDescription fontFamily="var(--font-inter)" color={theme.colors.actionGray} fontWeight={400}>
                {know.seoDescription ?? know.description}
            </KnowDescription>
        </div>
    )
}

export default KnowContent
